"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  interpretFeedHttp,
  FEED_PAGE_SIZE,
  feedTabSlug,
  isJunkUnmatchedFeedItem,
  type FeedFetchOutcome,
} from "@/lib/financialWork/feedClient";
import type { FinancialWorkFeed, FinancialWorkFeedItem } from "@/lib/financialWork/types";
import type { QueueErrorKind } from "@/lib/financialWork/queueClient";
import { useWorkspaceActivityAutoRefresh } from "@/hooks/useWorkspaceActivityAutoRefresh";
import { createMarketApiFetch } from "@/lib/marketApiFetch";
import { INCOME_MONEY_CHANGED_EVENT } from "@/lib/incomeMoneyRefresh";
import { getUserIdFromAuthToken } from "@/lib/getUserIdFromAuthToken";
import { subscribeAuthSession } from "@/lib/authSession";
import {
  subscribeReceivablePendingRefresh,
} from "@/lib/receivablePendingRefresh";

const marketApiFetch = createMarketApiFetch();

type FeedTabCounts = FinancialWorkFeed["counts"];

function emptyTabCounts(): FeedTabCounts {
  return { needs_action: 0, waiting: 0, recently_closed: 0, all: 0 };
}

const FEED_FRESH_MS = 12_000;
const feedPageCache = new Map<string, { feed: FinancialWorkFeed; at: number }>();
const feedInflight = new Map<string, Promise<FeedFetchOutcome>>();
let latestTabCounts = emptyTabCounts();
/** Monotonic clock for tab badges — older cached pages must not wipe fresher counts. */
let latestTabCountsAt = 0;

function feedPageKey(args: {
  workspaceId: string;
  bucket: string;
  q: string;
  page: number;
  pageSize: number;
}): string {
  return `${args.workspaceId}|${args.bucket}|${args.q}|${args.page}|${args.pageSize}`;
}

function normalizeTabCounts(
  counts: FinancialWorkFeed["counts"] | null | undefined,
): FeedTabCounts {
  return {
    needs_action: counts?.needs_action ?? 0,
    waiting: counts?.waiting ?? 0,
    recently_closed: counts?.recently_closed ?? 0,
    all: counts?.all ?? 0,
  };
}

/**
 * Keep the freshest tab badge totals. A stale waiting-page cache (e.g. still
 * ปิดล่าสุด=0 after a match) must not overwrite a newer feed response.
 */
function stampTabCounts(counts: FeedTabCounts, at: number): FeedTabCounts {
  if (at >= latestTabCountsAt) {
    latestTabCounts = counts;
    latestTabCountsAt = at;
    return counts;
  }
  return latestTabCounts;
}

function resetTabCounts() {
  latestTabCounts = emptyTabCounts();
  latestTabCountsAt = 0;
}

function dropFeedCache(workspaceId: string) {
  const prefix = `${workspaceId}|`;
  for (const key of [...feedPageCache.keys()]) {
    if (!workspaceId || key.startsWith(prefix)) feedPageCache.delete(key);
  }
}

/** Drop every cached งานเงิน tab page for a workspace (after match/close on another tab). */
export function invalidateFinancialWorkFeedCache(workspaceId?: string | null) {
  dropFeedCache(workspaceId?.trim() ?? "");
}

/** Reuse pages already loaded on งานเงิน for the prepare drawer. */
export function readCachedFeedItemsForWorkspace(
  workspaceId: string,
): FinancialWorkFeedItem[] {
  const prefix = `${workspaceId.trim()}|`;
  const byKey = new Map<string, FinancialWorkFeedItem>();
  for (const [key, entry] of feedPageCache) {
    if (!key.startsWith(prefix)) continue;
    for (const item of entry.feed.items ?? []) {
      if (isJunkUnmatchedFeedItem(item)) continue;
      const id = `${item.objectRef.kind}:${item.objectRef.id}`;
      byKey.set(id, item);
    }
  }
  return [...byKey.values()];
}

async function requestFeedPage(args: {
  workspaceId: string;
  ownerUserId?: string;
  actorId: string;
  bucket: string;
  q: string;
  page: number;
  pageSize: number;
}): Promise<FeedFetchOutcome> {
  const key = feedPageKey(args);
  const pending = feedInflight.get(key);
  if (pending) return pending;

  const run = (async (): Promise<FeedFetchOutcome> => {
    const qs = new URLSearchParams({
      workspace_id: args.workspaceId,
      limit: String(args.pageSize),
      page: String(Math.max(0, args.page)),
      actor_id: args.actorId,
    });
    if (args.ownerUserId) qs.set("owner_user_id", args.ownerUserId);
    if (args.q) qs.set("q", args.q);
    const res = await marketApiFetch(
      `/api/v1/financial-work/feed/${feedTabSlug(args.bucket)}?${qs.toString()}`,
      { cache: "no-store", headers: { Accept: "application/json" } },
    );
    const body = await res.json().catch(() => ({}));
    const outcome = interpretFeedHttp(res.status, body);
    if (outcome.kind === "ok") {
      const at = Date.now();
      const counts = stampTabCounts(normalizeTabCounts(outcome.feed.counts), at);
      const merged = { ...outcome.feed, counts };
      feedPageCache.set(key, { feed: merged, at });
      return { kind: "ok", feed: merged };
    }
    return outcome;
  })();

  feedInflight.set(key, run);
  try {
    return await run;
  } finally {
    if (feedInflight.get(key) === run) feedInflight.delete(key);
  }
}

/**
 * Loads one page of the active งานเงิน tab. Counts for every tab come with that
 * page, so the งานเงิน badge can refresh while the user is on ข้อมูล.
 */
export function useFinancialWorkFeed(args: {
  workspaceId: string | null;
  ownerUserId?: string | null;
  enabled?: boolean;
  refetchWhenVisible?: boolean;
  accessDenied?: boolean;
  page?: number;
  pageSize?: number;
  bucket?: string | null;
  q?: string | null;
}) {
  const {
    workspaceId,
    ownerUserId,
    enabled = true,
    refetchWhenVisible = false,
    accessDenied = false,
    page = 0,
    pageSize = FEED_PAGE_SIZE,
    bucket = "needs_action",
    q = "",
  } = args;
  const requestIdRef = useRef(0);
  const seenWorkspaceRef = useRef(workspaceId?.trim() ?? "");
  const visibleRef = useRef(refetchWhenVisible);
  const [feed, setFeed] = useState<FinancialWorkFeed | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(
    () => Boolean(enabled) && !accessDenied,
  );
  const [error, setError] = useState<string | null>(() =>
    accessDenied ? "ไม่มีสิทธิ์ดูคิวงานเงินนี้" : null,
  );
  const [errorKind, setErrorKind] = useState<QueueErrorKind | null>(() =>
    accessDenied ? "unauthorized" : null,
  );

  const workspaceKey = workspaceId?.trim() ?? "";
  const tabId = (bucket || "needs_action").trim() || "needs_action";
  const query = (q || "").trim();
  const currentPageKey = feedPageKey({
    workspaceId: workspaceKey,
    bucket: tabId,
    q: query,
    page,
    pageSize,
  });
  const pagingKeyRef = useRef(currentPageKey);
  if (seenWorkspaceRef.current !== workspaceKey) {
    dropFeedCache(seenWorkspaceRef.current);
    dropFeedCache(workspaceKey);
    seenWorkspaceRef.current = workspaceKey;
    setFeed(null);
    setUnavailable(false);
    setError(
      accessDenied && workspaceKey ? "ไม่มีสิทธิ์ดูคิวงานเงินนี้" : null,
    );
    setErrorKind(accessDenied && workspaceKey ? "unauthorized" : null);
    setLoading(Boolean(enabled && workspaceKey && !accessDenied));
    resetTabCounts();
  }

  const accessDeniedRef = useRef(accessDenied);
  if (accessDeniedRef.current !== accessDenied) {
    accessDeniedRef.current = accessDenied;
    if (accessDenied) {
      setFeed(null);
      setUnavailable(false);
      setError("ไม่มีสิทธิ์ดูคิวงานเงินนี้");
      setErrorKind("unauthorized");
      setLoading(false);
    } else {
      setError(null);
      setErrorKind(null);
      setLoading(Boolean(enabled && workspaceKey));
    }
  }

  const enabledRef = useRef(enabled);
  if (enabledRef.current !== enabled) {
    enabledRef.current = enabled;
    if (enabled && !accessDenied) {
      setError(null);
      setErrorKind(null);
      setLoading(Boolean(workspaceKey));
    }
  }

  const applyOutcome = useCallback((outcome: FeedFetchOutcome) => {
    if (outcome.kind === "ok") {
      // outcome.feed.counts already ran through stampTabCounts for live/cached pages.
      setFeed({
        ...outcome.feed,
        counts: normalizeTabCounts(outcome.feed.counts ?? latestTabCounts),
      });
      setUnavailable(false);
      setError(null);
      setErrorKind(null);
      return;
    }
    if (outcome.kind === "missing") {
      setFeed(null);
      setUnavailable(true);
      setError(null);
      setErrorKind(null);
      return;
    }
    if (outcome.kind === "unauthorized") {
      setFeed(null);
      setUnavailable(false);
      setError(outcome.message);
      setErrorKind("unauthorized");
      return;
    }
    throw new Error(outcome.message);
  }, []);

  const reload = useCallback(async (opts?: { silent?: boolean; force?: boolean }) => {
    if (!enabled) return;

    const ws = workspaceId?.trim() ?? "";
    const pagingKey = feedPageKey({
      workspaceId: ws,
      bucket: tabId,
      q: query,
      page,
      pageSize,
    });
    const pagingChanged = pagingKeyRef.current !== pagingKey;
    pagingKeyRef.current = pagingKey;
    const silent = opts?.silent ?? false;
    const force = Boolean(opts?.force);
    const cached = feedPageCache.get(pagingKey);
    const fresh = Boolean(cached && Date.now() - cached.at < FEED_FRESH_MS);

    if (cached && !force) {
      // Prefer fresher badge totals already seen on another tab over this page's
      // older snapshot (fixes ปิดล่าสุด flipping back to 0 after a match).
      const counts = stampTabCounts(
        normalizeTabCounts(cached.feed.counts),
        cached.at,
      );
      applyOutcome({
        kind: "ok",
        feed: { ...cached.feed, counts },
      });
      setLoading(false);
      // Same tab/page only: keep stale-while-revalidate. Tab switches must refetch.
      // Also refetch when cached badge totals are older than the latest live page.
      const countsStale = cached.at < latestTabCountsAt;
      if (!pagingChanged && fresh && silent && !countsStale) return;
    }

    const viewerId = getUserIdFromAuthToken()?.trim() || "";
    if (!ws) {
      setFeed(null);
      setUnavailable(false);
      setError(null);
      setErrorKind(null);
      setLoading(false);
      return;
    }
    if (accessDenied) {
      setFeed(null);
      setUnavailable(false);
      setError("ไม่มีสิทธิ์ดูคิวงานเงินนี้");
      setErrorKind("unauthorized");
      setLoading(false);
      return;
    }
    if (!viewerId) {
      if (!silent && !cached) {
        setFeed(null);
        setUnavailable(false);
        setError(null);
        setErrorKind(null);
        setLoading(true);
      }
      return;
    }

    if (!silent && !cached) {
      setUnavailable(false);
      setLoading(true);
      setError(null);
      setErrorKind(null);
    }
    const requestId = ++requestIdRef.current;
    try {
      const outcome = await requestFeedPage({
        workspaceId: ws,
        ownerUserId: ownerUserId?.trim() || undefined,
        actorId: viewerId,
        bucket: tabId,
        q: query,
        page,
        pageSize,
      });
      if (requestId !== requestIdRef.current) return;
      applyOutcome(outcome);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      if (cached) return;
      const message = err instanceof Error ? err.message : "โหลดรายการเงินไม่สำเร็จ";
      if (/failed to fetch|network|404|not found/i.test(message)) {
        setFeed(null);
        setUnavailable(true);
        setError(null);
        setErrorKind(null);
      } else if (!silent) {
        setError(message);
        setErrorKind("generic");
        setFeed(null);
        setUnavailable(false);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [accessDenied, applyOutcome, enabled, ownerUserId, page, pageSize, query, tabId, workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!enabled || accessDenied) return;
    if (getUserIdFromAuthToken()?.trim()) return;
    let timer = 0;
    const retry = () => {
      if (!getUserIdFromAuthToken()?.trim()) return;
      if (timer) window.clearInterval(timer);
      timer = 0;
      void reload();
    };
    const unsub = subscribeAuthSession(retry);
    timer = window.setInterval(retry, 150);
    return () => {
      unsub();
      if (timer) window.clearInterval(timer);
    };
  }, [accessDenied, enabled, reload]);

  useEffect(() => {
    const becameVisible = refetchWhenVisible && !visibleRef.current;
    visibleRef.current = refetchWhenVisible;
    if (!becameVisible) return;
    void reload({ silent: true });
  }, [refetchWhenVisible, reload]);

  useWorkspaceActivityAutoRefresh(enabled ? workspaceId : null, () =>
    reload({ silent: true }),
  );

  useEffect(() => {
    const bump = () => {
      dropFeedCache(workspaceKey);
      void reload({ silent: true, force: true });
    };
    window.addEventListener(INCOME_MONEY_CHANGED_EVENT, bump);
    const unsubPending = subscribeReceivablePendingRefresh(bump);
    return () => {
      window.removeEventListener(INCOME_MONEY_CHANGED_EVENT, bump);
      unsubPending();
    };
  }, [reload, workspaceKey]);

  return { feed, unavailable, loading, error, errorKind, reload };
}
